import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { Skeleton } from '@/design-system/primitives'

import { useEventContent, usePublicEvent } from '../api'
import { LegalBody } from '../pages/LegalPage'

/**
 * Lire ce qu'on accepte, sans quitter la page.
 *
 * Une case à cocher dont on ne peut pas lire le texte ne recueille rien : ni
 * un consentement valable au sens du RGPD, ni la confiance de celui qui coche.
 * Le lien ouvre donc le document **par-dessus** le tunnel, panier intact —
 * ouvrir un nouvel onglet ferait perdre le fil à qui remplit un formulaire sur
 * un téléphone, et abandonner sa commande est le geste le plus probable.
 *
 * Le texte reste par ailleurs accessible sur sa propre page, atteignable
 * depuis le pied de page : on doit pouvoir le lire, l'imprimer et le garder
 * sans passer par un achat.
 */
export const LEGAL_ROUTES: Record<string, string> = {
  'legal.terms': '/cgv',
  'legal.privacy': '/confidentialite',
  'legal.imprint': '/mentions-legales',
  'legal.contest': '/reglement-concours',
}

export function LegalLink({ blockKey, label }: { blockKey: string; label: string }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="underline underline-offset-2 hover:text-champagne-300"
      >
        {label}
      </button>
      {open ? <LegalDialog blockKey={blockKey} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function LegalDialog({ blockKey, onClose }: { blockKey: string; onClose: () => void }) {
  const { t } = useTranslation()
  const eventQuery = usePublicEvent()
  const content = useEventContent(eventQuery.data?.slug)

  const block = (content.data ?? []).find((row) => row.key === blockKey)

  // Échap ferme : c'est le premier réflexe devant une surcouche, et ne pas y
  // répondre donne l'impression d'être coincé dans le formulaire.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal
      aria-label={block?.title ?? t('legal.reading')}
    >
      <button
        type="button"
        aria-label={t('legal.close')}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="relative flex max-h-[88vh] w-full max-w-2xl flex-col rounded-t-lg border border-line bg-bg-base sm:rounded-lg">
        <header className="flex items-start justify-between gap-4 border-b border-line-subtle px-6 py-4">
          <h2 className="font-display text-heading-md text-content-primary">
            {block?.title ?? t('legal.reading')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('legal.close')}
          </Button>
        </header>

        <div className="overflow-y-auto px-6 py-6">
          {content.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : block ? (
            <LegalBody body={block.body} />
          ) : (
            <p className="text-content-secondary">{t('legal.missingBody')}</p>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line-subtle px-6 py-4">
          {/* La page entière reste atteignable : on doit pouvoir garder ce
              texte, l'imprimer, ou y revenir sans repasser par une commande. */}
          <Link
            to={LEGAL_ROUTES[blockKey] ?? '/'}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-content-muted underline underline-offset-2 hover:text-content-secondary"
          >
            {t('legal.openFullPage')}
          </Link>
          <Button size="sm" onClick={onClose}>
            {t('legal.done')}
          </Button>
        </footer>
      </div>
    </div>
  )
}
